import type { ThreadPullRequestKey, ThreadPullRequestLink } from "@afar-research/contracts";
import { chainPullRequestsByBaseBranch } from "./pullRequestChains.ts";

/**
 * Derivations over a thread's pull request collection: identity, visibility, stacks, and the
 * one-slot summaries a badge or copy-link target needs. Membership transitions live in
 * `threadPullRequestCollection.ts`; both are the AFAR Domain's only authority on these rules.
 *
 * Every comparison here is host-qualified. The owning Environment qualifies a key further:
 * two Environments holding the same host, repository, and number hold two memberships, and
 * nothing in this module may be used to collapse them.
 */

/** Case-insensitive on host and repository, exact on number. */
export function normalizeThreadPullRequestKey(key: ThreadPullRequestKey): ThreadPullRequestKey {
  return {
    host: key.host.trim().toLowerCase(),
    repository: key.repository.trim().toLowerCase(),
    number: key.number,
  };
}

export function threadPullRequestKeyOf(key: ThreadPullRequestKey): string {
  const normalized = normalizeThreadPullRequestKey(key);
  return `${normalized.host}/${normalized.repository}#${normalized.number}`;
}

export function threadPullRequestKeysEqual(
  left: ThreadPullRequestKey,
  right: ThreadPullRequestKey,
): boolean {
  return threadPullRequestKeyOf(left) === threadPullRequestKeyOf(right);
}

/**
 * The links a user sees. Tombstoned members stay in the collection so stack discovery cannot
 * re-add them, and are never shown.
 */
export function visibleThreadPullRequests(
  links: ReadonlyArray<ThreadPullRequestLink>,
): ReadonlyArray<ThreadPullRequestLink> {
  return links.filter((link) => link.source !== "stack-dismissed");
}

export interface ThreadPullRequestChain {
  readonly kind: "native" | "derived";
  /** Bottom to top. */
  readonly layers: ReadonlyArray<ThreadPullRequestLink>;
}

/**
 * Groups visible links into stacks. Native stack metadata read from the host wins; the rest
 * are chained by matching one link's base branch to another's head branch inside the same
 * host and repository. Branch matching only orders links that are already members: it never
 * discovers a pull request. A link that chains to nothing is a one-layer chain, and an
 * ambiguous parent or a cycle leaves its links unchained rather than inventing an order.
 */
export function resolveThreadPullRequestChains(
  links: ReadonlyArray<ThreadPullRequestLink>,
): ReadonlyArray<ThreadPullRequestChain> {
  const visible = visibleThreadPullRequests(links);
  const chains: Array<ThreadPullRequestChain> = [];
  const placed = new Set<string>();

  const nativeStacks = new Map<string, Array<ThreadPullRequestLink>>();
  for (const link of visible) {
    if (link.stack === null) continue;
    const key = normalizeThreadPullRequestKey(link);
    const stackKey = `${key.host}/${key.repository}#stack:${link.stack.id}`;
    const members = nativeStacks.get(stackKey) ?? [];
    members.push(link);
    nativeStacks.set(stackKey, members);
  }
  for (const members of nativeStacks.values()) {
    const order = new Map(members[0]!.stack!.layers.map((layer, index) => [layer.number, index]));
    members.sort((left, right) => (order.get(left.number) ?? 0) - (order.get(right.number) ?? 0));
    for (const member of members) placed.add(threadPullRequestKeyOf(member));
    chains.push({ kind: "native", layers: members });
  }

  // Whatever no host called a stack is chained by base branch, under the same rule a workspace
  // listing uses: the relation is the pull requests', not this collection's.
  const remaining = visible.filter((link) => !placed.has(threadPullRequestKeyOf(link)));
  for (const layers of chainPullRequestsByBaseBranch(remaining, {
    key: threadPullRequestKeyOf,
    scope: (link) => {
      const key = normalizeThreadPullRequestKey(link);
      return `${key.host}/${key.repository}`;
    },
    headBranch: (link) => link.observation?.headBranch ?? null,
    baseBranch: (link) => link.observation?.baseBranch ?? null,
  })) {
    chains.push({ kind: "derived", layers });
  }
  return chains;
}

/**
 * The URL of another pull request in the same repository, derived from one we already hold.
 * Returns null when the link's URL is not a pull request page, so a stack sibling is skipped
 * rather than linked under a guessed address.
 */
export function siblingPullRequestUrl(url: string, number: number): string | null {
  const match = /^(?<prefix>.*\/pull\/)\d+(?<suffix>[/?#].*)?$/u.exec(url);
  const prefix = match?.groups?.["prefix"];
  return prefix === undefined ? null : `${prefix}${number}`;
}

function isOpenThreadPullRequest(link: ThreadPullRequestLink): boolean {
  // A link with no observation counts as open: it was just linked, and hiding it behind a
  // terminal pull request until the first read would make the link look lost.
  return link.observation === null || link.observation.state === "open";
}

function latestObservedAt(link: ThreadPullRequestLink): number {
  const value = link.observation?.updatedAt ?? link.linkedAt;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? 0 : ms;
}

/** The one pull request a single-slot surface shows: a sidebar badge, a tab icon, a copy target. */
export type ThreadCurrentPullRequest =
  | { readonly kind: "single"; readonly link: ThreadPullRequestLink }
  | {
      readonly kind: "stack";
      readonly open: ReadonlyArray<ThreadPullRequestLink>;
      /** Highest open layer: what "View pull request" and copy-reference address. */
      readonly top: ThreadPullRequestLink;
    };

/**
 * Prefers open work, and within a chain the highest open layer. A finished chain still points
 * at its top; unrelated terminal links fall back to the most recently updated one.
 */
export function resolveThreadCurrentPullRequest(
  links: ReadonlyArray<ThreadPullRequestLink>,
): ThreadCurrentPullRequest | null {
  const visible = visibleThreadPullRequests(links);
  if (visible.length === 0) return null;
  const open = visible.filter(isOpenThreadPullRequest);
  if (open.length === 1) return { kind: "single", link: open[0]! };
  const chains = resolveThreadPullRequestChains(visible);
  if (open.length > 1) {
    const openChains = chains
      .map((chain) => chain.layers.toReversed().filter(isOpenThreadPullRequest))
      .filter((layers) => layers.length > 0)
      .sort(
        (left, right) =>
          Math.max(...right.map((link) => Date.parse(link.linkedAt))) -
          Math.max(...left.map((link) => Date.parse(link.linkedAt))),
      );
    const ordered = openChains.flat();
    return { kind: "stack", open: ordered, top: ordered[0]! };
  }
  if (chains.length === 1) {
    return { kind: "single", link: chains[0]!.layers.at(-1)! };
  }
  const terminal = [...visible].sort(
    (left, right) => latestObservedAt(right) - latestObservedAt(left),
  );
  return { kind: "single", link: terminal[0]! };
}

/** The single link a one-slot consumer should address, or null when the thread has none. */
export function resolveThreadCurrentPullRequestLink(
  links: ReadonlyArray<ThreadPullRequestLink>,
): ThreadPullRequestLink | null {
  const current = resolveThreadCurrentPullRequest(links);
  if (current === null) return null;
  return current.kind === "single" ? current.link : current.top;
}

export type ThreadPullRequestBadge = {
  readonly state: "open" | "closed" | "merged" | "draft";
} & (
  | { readonly kind: "stack"; readonly layers: number }
  | { readonly kind: "pull-request"; readonly others: number }
);

/**
 * One badge for a thread's whole collection: the aggregate state, and whether the links form
 * a single stack or several unrelated pull requests.
 */
export function resolveThreadPullRequestBadge(
  pullRequests: ReadonlyArray<ThreadPullRequestLink> | undefined,
): ThreadPullRequestBadge | null {
  const visible = visibleThreadPullRequests(pullRequests ?? []);
  if (visible.length === 0) return null;
  const states = visible.map((link) => link.observation?.state ?? "open");
  const state = visible.every(
    (link) => link.observation?.state === "open" && link.observation.isDraft,
  )
    ? "draft"
    : states.includes("open")
      ? "open"
      : states.every((entry) => entry === "merged")
        ? "merged"
        : "closed";
  const chains = resolveThreadPullRequestChains(visible);
  if (visible.length > 1 && chains.length === 1) {
    return { kind: "stack", layers: visible.length, state };
  }
  return { kind: "pull-request", others: visible.length - 1, state };
}

/**
 * Terms that let a thread be found by its pull requests, so searching the sidebar or the
 * command palette for a number, a repository, or a title reaches the thread holding it.
 */
export function threadPullRequestSearchTerms(thread: {
  readonly pullRequests?: ReadonlyArray<ThreadPullRequestLink> | undefined;
}): ReadonlyArray<string> {
  return visibleThreadPullRequests(thread.pullRequests ?? []).flatMap((link) => [
    `#${link.number}`,
    `${link.repository}#${link.number}`,
    link.url,
    link.observation?.title ?? "",
  ]);
}
